class SliderComponent extends HTMLElement {
  constructor() {
    super();

    if (this.classList.contains("slideshow")) {
      this.slider = this;
    } else {
      this.slider = this.querySelector('[id^="Slider-"]');
    }

    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.enableDrag = this.dataset.draggable === "true" ? true : false;

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));

    if (this.enableDrag) {
      this.addClickAndDrag();
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton?.setAttribute("disabled", "disabled");
    } else {
      this.prevButton?.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton?.setAttribute("disabled", "disabled");
    } else {
      this.nextButton?.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  connectedCallback() {
    // Fix a bug where carousels are sometimes scrolled to the far left on page load
    if (this.dataset.reset_scroll == "true") {
      this.slider.scrollLeft = 0;
    }
  }

  scrollToFullyVisibleLi() {
    const lis = this.slider.querySelectorAll("li");
    for (let i = 0; i < lis.length; i++) {
      const li = lis[i];
      const rect = li.getBoundingClientRect();
      if (rect.left >= 32 && rect.right <= this.slider.clientWidth) {
        li.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
          inlineMargin: -32,
        });
        break;
      }
    }
  }

  scrollToPartiallyVisibleLi() {
    const lis = this.slider.querySelectorAll("li");
    for (let i = 0; i < lis.length; i++) {
      const li = lis[i];
      const rect = li.getBoundingClientRect();
      // Check if the right edge of the element is within the viewport
      if (rect.left < this.slider.clientWidth && rect.right > 0) {
        li.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
          inlineMargin: -32,
        });
        break;
      }
    }
  }

  scrollToPartiallyOrFullyVisibleLi(startScrollLeft, endScrollLeft) {
    const isDraggingRight = endScrollLeft > startScrollLeft;

    // return if distance is too small
    if (Math.abs(endScrollLeft - startScrollLeft) < 10) {
      return;
    };

    const lis = this.slider.querySelectorAll("li");
    let targetLi = null;

    // Find the first li element that is partially or fully in view based on dragging direction
    for (let i = 0; i < lis.length; i++) {
      const li = lis[i];
      const rect = li.getBoundingClientRect();

      if (isDraggingRight) {
        // If dragging right, find the first partially in view element
        if (rect.left >= 32 && rect.left < this.slider.clientWidth) {
          targetLi = li;
          break;
        }
      } else {
        if (rect.left < this.slider.clientWidth && rect.right > 0) {
          targetLi = li;
          break;
        }
      }
    }

    if (targetLi) {
      targetLi.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
        inlineMargin: -32,
      });
    }
  }

  addClickAndDrag() {
    let isDown = false;
    let startX;
    let startScrollLeft;
    let isDragging = false;

    const slider = this.slider;

    let timer;
    let holdTime = 50; // Adjust this value (in milliseconds) as needed

    slider.addEventListener("mousedown", (e) => {
      if (window.innerWidth <= 749) return;

      timer = setTimeout(() => {
        isDown = true;
        isDragging = false;
        slider.classList.add("active");
        slider.classList.add("grabbing");
        startX = e.pageX;
        startScrollLeft = slider.scrollLeft;
        slider.style.scrollSnapType = "none";
        slider.style.scrollBehavior = "auto";
      }, holdTime);

      e.preventDefault();
    });

    slider.addEventListener("mouseup", () => {
      if (window.innerWidth <= 749) return;
      clearTimeout(timer);

      if (isDown) {
        isDown = false;
        slider.classList.remove("active");
        slider.classList.remove("grabbing");
        slider.style.scrollBehavior = "";
        console.log(startScrollLeft < slider.scrollLeft)
        this.scrollToPartiallyOrFullyVisibleLi(
          startScrollLeft, slider.scrollLeft
        );
        setTimeout(() => {
          slider.style.scrollSnapType = "";
          document.body.classList.remove("dragging");
        }, 500);
        setTimeout(() => {
          isDragging = false;
        }, 0);
      }
    });

    slider.addEventListener("mouseleave", () => {
      clearTimeout(timer);
      if (window.innerWidth <= 749) return;
      if (isDown) {
        isDown = false;
        slider.classList.remove("active");
        slider.classList.remove("grabbing");
        slider.style.scrollBehavior = "";

        this.scrollToPartiallyOrFullyVisibleLi(
          startScrollLeft, slider.scrollLeft
        );
        setTimeout(() => {
          slider.style.scrollSnapType = "";
          document.body.classList.remove("dragging");
        }, 500);
        setTimeout(() => {
          isDragging = false;
        }, 0);
      }
    },
    { passive: true }
  );

    slider.addEventListener("mousemove", (e) => {
      if (window.innerWidth <= 749) return;
      if (!isDown) return;
      e.preventDefault();
      isDragging = true;
      document.body.classList.add("dragging");
      const x = e.pageX;
      const walk = x - startX;
      slider.scrollLeft = startScrollLeft - walk;
    });

    slider.addEventListener("click", (e) => {
      if (window.innerWidth <= 749) return;
      if (isDragging && e.target.matches("button[data-media-id]")) {
        e.preventDefault();
        e.stopPropagation(); // Stop the event from bubbling up
      }
    });

    slider.querySelectorAll("a").forEach(
      (link) => {
        link.addEventListener("click", (e) => {
          if (window.innerWidth <= 749) return;
          if (isDragging) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        });
      },
      { passive: true }
    );
  }
}

customElements.define("slider-component", SliderComponent);

class SliderProgressBar extends HTMLElement {
  constructor() {
    super();
    this.sliderElId = this.dataset.sliderElId;
    this.sliderEl = document.getElementById(this.sliderElId);

    if (!this.sliderEl) {
      console.error('Slider element with id "' + this.sliderElId + '" not found');
      return;
    }

    this.sliderElUL = this.sliderEl.querySelector("ul");
    this.sliderItems = this.sliderEl.querySelectorAll("ul > li");
    this.totalCards = this.sliderItems.length;

    setTimeout(() => {
      this.totalCardsVisible = this.visibleCardsCount();
      this.createThumb();
      this.sliderElUL.addEventListener("scroll", this.updateThumbPosition.bind(this));
    }, 200);
    window.addEventListener("resize", this.handleResize.bind(this));

    this.isDragging = false;
  }

  visibleCardsCount() {
    let visibleCards = 0;
    this.sliderItems.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      const sliderRect = this.sliderEl.getBoundingClientRect();
      if (cardRect.left >= sliderRect.left && cardRect.right <= sliderRect.right) {
        visibleCards++;
      }
    });
    return visibleCards;
  }

  createThumb() {
    this.thumb = document.createElement("div");
    this.thumb.className = "slider-progress-bar__thumb";
    this.updateThumbVisibility();
    this.appendChild(this.thumb);

    // Mouse Events
    this.thumb.addEventListener("mousedown", this.handleMouseDown.bind(this));
    document.addEventListener("mouseup", this.handleMouseUp.bind(this));
    document.addEventListener("mousemove", this.handleMouseMove.bind(this));

    // Touch Events
    this.thumb.addEventListener("touchstart", this.handleTouchStart.bind(this), { passive: false });
    document.addEventListener("touchend", this.handleTouchEnd.bind(this));
    document.addEventListener("touchmove", this.handleTouchMove.bind(this), { passive: false });
  }

  updateThumbVisibility() {
    this.totalCardsVisible = this.visibleCardsCount();
    const thumbWidth = (this.totalCardsVisible / this.totalCards) * 100;

    if (thumbWidth >= 100) {
      this.style.display = "none";
    } else {
      this.style.display = "block";
      this.thumb.style.width = `${thumbWidth}%`;
    }
  }

  updateThumbPosition() {
    const sliderWidth = this.sliderElUL.clientWidth;
    const scrollWidth = this.sliderElUL.scrollWidth;
    const scrollLeft = this.sliderElUL.scrollLeft;
    const progressBarWidth = this.clientWidth;

    const scrollPercentage = scrollLeft / (scrollWidth - sliderWidth);
    const thumbLeft = scrollPercentage * (progressBarWidth - this.thumb.clientWidth);

    this.thumb.style.left = `${thumbLeft}px`;
  }

  handleResize() {
    this.updateThumbVisibility();
    this.updateThumbPosition();
  }

  handleMouseDown(event) {
    event.preventDefault();
    this.isDragging = true;
  }

  handleMouseMove(event) {
    if (!this.isDragging) return;
    this.updateScrollPosition(event.clientX);
  }

  handleMouseUp() {
    this.isDragging = false;
  }

  handleTouchStart(event) {
    event.preventDefault();
    this.isDragging = true;
  }

  handleTouchMove(event) {
    if (!this.isDragging) return;
    this.updateScrollPosition(event.touches[0].clientX);
  }

  handleTouchEnd() {
    this.isDragging = false;
  }

  updateScrollPosition(clientX) {
    const progressBarRect = this.getBoundingClientRect();
    const progressBarWidth = progressBarRect.width;
    const thumbWidth = this.thumb.clientWidth;
    let thumbLeft = clientX - progressBarRect.left - thumbWidth / 2;

    thumbLeft = Math.max(0, Math.min(thumbLeft, progressBarWidth - thumbWidth));

    const scrollPercentage = thumbLeft / (progressBarWidth - thumbWidth);
    const scrollWidth = this.sliderElUL.scrollWidth;
    const sliderWidth = this.sliderElUL.clientWidth;
    const scrollLeft = scrollPercentage * (scrollWidth - sliderWidth);

    this.sliderElUL.scrollTo({ left: scrollLeft });
    this.updateThumbPosition();
  }
}

customElements.define("slide-progress-bar", SliderProgressBar);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    // Check if the slideshow should be disabled
    this.disableSlideshow = this.dataset.disableSlideshow
    if (this.disableSlideshow && this.disableSlideshow === "true") {
      return;
    }

    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton?.addEventListener(
      "click",
      this.autoPlayToggle.bind(this)
    );
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton?.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton ||
      this.sliderAutoplayButton?.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton ||
      this.sliderAutoplayButton?.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton?.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton?.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton?.classList.remove(
        "slideshow__autoplay--paused"
      );
      this.sliderAutoplayButton?.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft +
          this.slider.querySelector(".slideshow__slide").clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (
            !this.querySelector("slideshow-component") &&
            this.classList.contains("complementary-products")
          ) {
            this.remove();
          }

          // Number of cards loaded will be cards.length
          const cards = html.querySelectorAll(".grid__item");

          // When the section is re-loaded with products, liquid within cannot reach the outer feature wrap element
          // Need to add fit-exactly classes with js depending on how many products are loaded
          // With the extra class a width for showing an extra quarter or half overrides the exact fit width
          const feature_card_enabled =
            this.dataset.feature_card_enabled === "true";
          if (feature_card_enabled) {
            const columnsDesktop = parseInt(this.dataset.columnsDesktop);
            const cards_fit_desktop = cards.length <= columnsDesktop;
            const feature_wrap_el = document.querySelector(
              `#shopify-section-${this.dataset.section_id} .carousel__feature-wrap`
            );
            if (cards_fit_desktop) {
              feature_wrap_el.classList.add(
                "carousel__feature-wrap--cards-fit-desktop"
              );
            }
          }

          const parent_section = document.getElementById(
            this.dataset.section_id
          );

          // Need to check recently tab before hiding the whole thing
          const hide_recently_tab = this.dataset.hide_recently_tab === "true";
          const product_handle = this.dataset.product_handle;
          const product_handles_list = localStorage.getItem(
            "shopify_recently_viewed"
          );
          const no_recently_viewed =
            !product_handles_list ||
            product_handles_list == "" ||
            product_handles_list == product_handle;

          if (cards) {
            // Loaded
            this.classList.add("product-recommendations--loaded");
          } else if (hide_recently_tab || no_recently_viewed) {
            // No items - recently not showing - hide the carousel
            if (parent_section) {
              parent_section.classList.add("hidden");
            }
          } else {
            // hide just product recommendations
            // hide both tabs
            const recommendations = parent_section.querySelector(
              "product-recommendations"
            );
            const recently = parent_section.querySelector("recently-viewed");
            const recommendations_tab = parent_section.querySelector(
              ".product-recommendations-tab"
            );
            const recently_tab = parent_section.querySelector(
              ".recently-viewed-tab"
            );

            recommendations_tab.classList.add("hidden");
            recently_tab.classList.add("hidden");
            recommendations.classList.remove("active");
            recently.classList.add("active");
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class UpsellProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector(
          "upsell-product-recommendations"
        );

        if (recommendations) {
          if (recommendations.innerHTML.trim().length == 0) {
            fetch(this.dataset.fallbackurl)
              .then((response) => response.text())
              .then((text) => {
                html.innerHTML = text;
                const recommendations = html.querySelector(
                  "upsell-product-recommendations"
                );
                this.innerHTML = recommendations.innerHTML;
                if (
                  !this.querySelector("slideshow-component") &&
                  this.classList.contains("complementary-products")
                ) {
                  this.remove();
                }
                if (html.querySelector(".grid__item")) {
                  this.classList.add("product-recommendations--loaded");
                }
              });
          } else {
            this.innerHTML = recommendations.innerHTML;
            if (
              !this.querySelector("slideshow-component") &&
              this.classList.contains("complementary-products")
            ) {
              this.remove();
            }
            if (html.querySelector(".grid__item")) {
              this.classList.add("product-recommendations--loaded");
            }
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define(
  "upsell-product-recommendations",
  UpsellProductRecommendations
);